<template>
  <Transition
    name="fade-medium"
    mode="out-in"
    appear
  >
    <div
      v-show="messengerIsVisible"
      class="fixed inset-0 bg-primary-dark opacity-50 z-4000 intercom-window:block"
      @click="onBackgroundOverlayPress"
    ></div>
  </Transition>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import loadExternalScript from '@/utils/loadExternalScript';
import { addClass, removeClass } from '@/utils/dom';

const launcherIsVisibleDefault = false;

export default {
  mounted() {
    this.bootIntercom();
  },
  watch: {
    userAuthData: {
      immediate: true,
      handler() {
        this.updateIntercomData();
      },
    },
    userAuthDataId: {
      immediate: false,
      handler(newValue) {
        if (newValue) return;

        const { Intercom } = window;

        if (!Intercom) return;

        // Clearing Intercom messages data if user is signing out
        Intercom('shutdown');
        this.bootIntercom();
      },
    },
    messengerIsVisible: {
      immediate: true,
      handler(newValue) {
        const { Intercom } = window;

        if (!Intercom) return;

        if (newValue) {
          addClass(document.body, 'overflow-hidden');
        } else {
          removeClass(document.body, 'overflow-hidden');
        }

        if (!newValue) {
          Intercom('hide');
          return;
        }

        Intercom('show');
      },
    },
  },
  computed: {
    ...mapState('intercom', {
      messengerIsVisible: 'messengerIsVisible',
    }),
    ...mapState('user/auth', {
      userAuthData: 'authData',
    }),
    ...mapGetters('user/auth', {
      userAuthDataId: 'authDataId',
    }),
  },
  methods: {
    ...mapActions('intercom', {
      updateMessengerIsVisible: 'updateMessengerIsVisible',
      updateUnreadMessagesCount: 'updateUnreadMessagesCount',
    }),
    bootIntercom() {
      loadExternalScript(`https://widget.intercom.io/widget/${process.env.VUE_APP_INTERCOM_APP_ID}`)
        .then(() => {
          const { Intercom } = window;

          if (!Intercom) return;

          Intercom('boot', {
            app_id: process.env.VUE_APP_INTERCOM_APP_ID,
            // hide_default_launcher: !launcherIsVisibleDefault,
          });
          this.updateIntercomData();

          this.updateMessengerIsVisible(launcherIsVisibleDefault);

          // Setting up Intercom listeners
          Intercom('onShow', () => {
            if (this.messengerIsVisible) return;

            this.updateMessengerIsVisible(true);
          });
          Intercom('onHide', () => {
            if (!this.messengerIsVisible) return;

            this.updateMessengerIsVisible(false);
          });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    updateIntercomData() {
      const { Intercom } = window;

      if (!Intercom || !this.userAuthData.id) return;

      Intercom('update', {
        app_id: process.env.VUE_APP_INTERCOM_APP_ID,
        user_id: this.userAuthData.id,
        name: this.userAuthData.displayName,
        email: this.userAuthData.email,
      });
    },
    onBackgroundOverlayPress() {
      this.updateMessengerIsVisible(false);
    },
  },
};
</script>

<style>
/* intercom-borderless-frame: Only appears when a message was sent to the user while having the chat closed */

/* Hide launcher by default */
.layout-app .intercom-namespace {
  .intercom-launcher-frame {
    @apply hidden !important;
  }

  .intercom-borderless-frame {
    @apply max-h-250 top-header inset-x-0 w-full bg-gray-200 rounded-lg shadow-12dp !important;
  }

  /* Hide badge count */
  .intercom-launcher-badge-frame {
    @apply hidden
  }
}

/* Base override (Intercom breakpoint at 451px) */
@screen intercom-large {
  .layout-app .intercom-namespace {
    .intercom-messenger-frame {
      @apply h-auto top-20 right-20 bottom-130 left-auto !important;
    }

    .intercom-borderless-frame {
      @apply right-30 left-auto max-h-275 w-auto py-5 px-10 !important;
    }
  }
}
</style>
